import type { TAbstractWrapper } from '@Anarchy/Engine/Abstract';
import { AbstractWrapper } from '@Anarchy/Engine/Abstract';
import { lightEntityToConfig } from '@Anarchy/Engine/Light/Adapters';
import type { LightType } from '@Anarchy/Engine/Light/Constants';
import type { TAbstractLightConfig, TAbstractLightWrapper, TAnyLight, TLightParams, TLightServiceDependencies, TLightTransformDrive } from '@Anarchy/Engine/Light/Models';
import { LightTransformDrive } from '@Anarchy/Engine/Light/TransformDrive';
import { getWrapperType } from '@Anarchy/Engine/Light/Utils';
import { applyShadowParams } from '@Anarchy/Engine/Light/Wrappers/LightWrapperHelper';
import { withObject3d } from '@Anarchy/Engine/Mixins';
import type { TDriveToTargetConnector } from '@Anarchy/Engine/TransformDrive';
import { DriveToTargetConnector } from '@Anarchy/Engine/TransformDrive';
import { applyObject3dParams } from '@Anarchy/Engine/Utils';
import type { Subscription } from 'rxjs';

export function AbstractLightWrapper<T extends TAnyLight>(entity: T, params: TLightParams, dependencies: TLightServiceDependencies): TAbstractLightWrapper<T> {
  const wrapper: TAbstractWrapper<T> = AbstractWrapper(entity, getWrapperType(entity), params);
  const drive: TLightTransformDrive = LightTransformDrive(params, dependencies, wrapper.id);
  const driveToTargetConnector: TDriveToTargetConnector = DriveToTargetConnector(drive, entity);

  // eslint-disable-next-line functional/immutable-data
  const result: TAbstractLightWrapper<T> = Object.assign(wrapper, {
    drive,
    driveToTargetConnector,
    ...withObject3d(entity),
    entity,
    getType: (): LightType => entity.type as LightType,
    serialize: (): TAbstractLightConfig<T> => lightEntityToConfig(result) as TAbstractLightConfig<T>
  });

  const destroySub$: Subscription = result.destroy$.subscribe((): void => {
    destroySub$.unsubscribe();

    (entity as any).shadow?.map?.dispose();
    entity.dispose();
  });

  applyShadowParams(params, result.entity);
  applyObject3dParams(result, params);

  return result;
}
